import {useEffect, useState} from 'react';
import {DriveFile, DriveRevision} from '../../drive/types';
import {useImageBlob, useRevisionImageBlob} from '../../imageCache/useImageBlob';
import {peekCachedImage} from '../../imageCache/blobCache';
import {buildLargePreviewUrl} from '../../imageCache/largePreviewUrl';
import {canDisplayInBrowser, chooseViewerSource, ViewerSource} from '../../domain/viewerImage';

// Preview shown by default: a large but still server-resized/compressed image, not the
// original file — the "Original" toggle in the top bar opts into the full download.
const PREVIEW_SIZE = 2000;

// How many of the following photos' previews to warm up (plus the previous one).
const PREFETCH_AHEAD = 3;

interface ViewerImageInput {
    files: DriveFile[];
    index: number;
    /** The info panel is shown for a photo Drive has no metadata for: it reads EXIF from the original. */
    needsMetadata: boolean;
    /** The photo is zoomed in: its original is fetched (but not its neighbours') and shown once there. */
    zoomed: boolean;
    /** An older version of the photo to show instead of its current content (downloaded whole). */
    revision?: DriveRevision;
}

export interface ViewerImage {
    source: ViewerSource;
    /** The original is a format the browser can show. */
    displayable: boolean;
    /** The image to show: the original or Drive's preview, per `source`. Undefined while the original loads. */
    displayUrl: string | undefined;
    /** Drive's preview, shown under the original until that has loaded. */
    placeholderUrl: string | undefined;
    /** Loading the shown original failed. */
    loadError: Error | null;
    /** Call when the preview (`displayUrl` with the preview source) fails to load. */
    onPreviewError: () => void;
    /** The original is being fetched for a zoom, while the preview is still shown. */
    zoomOriginalLoading: boolean;
    /** The "Eredeti" switch: on as set, or locked on when there is no usable preview. */
    originalSwitch: { checked: boolean; disabled: boolean; toggle: () => void };
    /** The original, for the info panel's EXIF. */
    blob: Blob | undefined;
    blobError: Error | null;
    /** The older version shown instead of the current content, if any. */
    revisionId: string | undefined;
    /** That version is being downloaded (the stage may keep showing what it showed before). */
    revisionLoading: boolean;
}

/**
 * What the viewer shows of the photo at `index` — Drive's large preview, or the original when asked
 * for, zoomed into, or already downloaded, or an older version picked in the versions panel — and
 * the downloads that go with it.
 */
export function useViewerImage({files, index, needsMetadata, zoomed, revision}: ViewerImageInput): ViewerImage {
    // Stays on across photos once turned on.
    const [showOriginal, setShowOriginal] = useState(false);
    // Kept with its URL, so it never applies to the next photo's preview.
    const [failedPreviewUrl, setFailedPreviewUrl] = useState<string>();

    const current = index >= 0 ? files[index] : undefined;
    const prev = index > 0 ? files[index - 1] : undefined;
    const next = index >= 0 && index < files.length - 1 ? files[index + 1] : undefined;

    // Originals are only ever downloaded for display in formats the browser can show (no RAW).
    const displayable = !!current && canDisplayInBrowser(current);
    // An older version has no preview of its own (Drive's thumbnail is of the current content).
    const revisionDisplayable = !!current && !!revision && canDisplayInBrowser({
        name: revision.originalFilename ?? current.name,
        mimeType: revision.mimeType ?? current.mimeType,
    });
    const olderVersion = useRevisionImageBlob(
        revisionDisplayable ? current?.id : undefined,
        revisionDisplayable ? revision?.id : undefined
    );
    const previewUrl = buildLargePreviewUrl(current?.thumbnailLink, PREVIEW_SIZE);
    const previewFailed = !!previewUrl && failedPreviewUrl === previewUrl;
    const originalCached = !!current && !!peekCachedImage(current.id);
    const source = chooseViewerSource({
        displayable,
        showOriginal,
        originalCached,
        hasPreview: !!previewUrl,
        previewFailed,
    });
    const showsOriginal = source === 'original';
    // Shown because there is no usable preview, not because it was asked for: the switch shows it as on, locked.
    const originalForced = showsOriginal && !showOriginal && !originalCached;
    // Only a photo Drive has no metadata for needs its original for the info panel — fetched quietly.
    const metadataNeedsBlob = needsMetadata && displayable;
    // Zooming fetches only this photo's original; once cached, `chooseViewerSource` switches to it.
    // An older version shown is whole already.
    const zoomNeedsBlob = zoomed && displayable && !revision;
    const prefetchOriginals = showsOriginal && showOriginal;

    const original = useImageBlob(
        (showsOriginal && !revision) || metadataNeedsBlob || zoomNeedsBlob ? current?.id : undefined,
        prefetchOriginals && prev && canDisplayInBrowser(prev) ? prev.id : undefined,
        prefetchOriginals && next && canDisplayInBrowser(next) ? next.id : undefined
    );

    // Warm the browser's HTTP cache with the next few previews (and the previous one) so stepping
    // through them feels instant. These come from Google's thumbnail server, not the Drive API.
    const previewPrefetchUrls = (index >= 0 ? [...files.slice(index + 1, index + 1 + PREFETCH_AHEAD), prev] : [])
        .map((file) => buildLargePreviewUrl(file?.thumbnailLink, PREVIEW_SIZE))
        .filter((url): url is string => !!url);
    const previewPrefetchKey = previewPrefetchUrls.join('\n');
    useEffect(() => {
        if (!previewPrefetchKey) return;
        previewPrefetchKey.split('\n').forEach((url) => {
            const img = new window.Image();
            img.src = url;
            img.referrerPolicy = 'no-referrer';
        });
    }, [previewPrefetchKey]);

    const usablePreview = previewUrl && !previewFailed ? previewUrl : undefined;
    const blobs = {blob: original.blob, blobError: original.error};
    if (revision) {
        return {
            source: revisionDisplayable ? 'original' : 'unavailable',
            displayable: revisionDisplayable,
            displayUrl: olderVersion.url,
            placeholderUrl: undefined,
            loadError: olderVersion.error,
            onPreviewError: () => undefined,
            zoomOriginalLoading: false,
            // The version is shown whole: the switch shows it as on, locked.
            originalSwitch: {checked: true, disabled: true, toggle: () => setShowOriginal((v) => !v)},
            ...blobs,
            revisionId: revision.id,
            revisionLoading: olderVersion.isLoading,
        };
    }
    return {
        source,
        displayable,
        displayUrl: showsOriginal ? original.url : source === 'preview' ? previewUrl : undefined,
        placeholderUrl: showsOriginal ? usablePreview : undefined,
        loadError: showsOriginal ? original.error : null,
        onPreviewError: () => setFailedPreviewUrl(previewUrl),
        zoomOriginalLoading: zoomNeedsBlob && !showsOriginal && original.isLoading,
        originalSwitch: {
            checked: showOriginal || originalForced,
            disabled: !displayable || originalForced,
            toggle: () => setShowOriginal((v) => !v),
        },
        ...blobs,
        revisionId: undefined,
        revisionLoading: false,
    };
}
